import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Mic, FileText, Zap, Settings, LogIn, ChevronRight, Check, 
  TrendingUp, Crown, Sparkles, PenTool, Mail, Lock, User, 
  ArrowLeft, Loader2, Eye, EyeOff, X
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // login, signup, forgot
  const [showPassword, setShowPassword] = useState(false);
  
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const handleGoogleLogin = () => {
    setIsLoggingIn(true);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Use XMLHttpRequest to avoid Emergent preview script fetch interception
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/auth/login`);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        xhr.timeout = 30000; // 30 second timeout
        
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            text: xhr.responseText
          });
        };
        
        xhr.onerror = () => {
          console.error("XHR onerror triggered");
          reject(new Error("Network error - please check your connection"));
        };
        
        xhr.ontimeout = () => {
          console.error("XHR timeout triggered");
          reject(new Error("Request timed out - please try again"));
        };
        
        xhr.send(JSON.stringify({ email, password }));
      });
      
      if (!result.ok) {
        let errorMessage = "Login failed. Please try again.";
        try {
          const errorData = JSON.parse(result.text);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Response wasn't valid JSON
        }
        throw new Error(errorMessage);
      }
      
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "Login failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    
    // Check terms agreement first
    if (!agreedToTerms) {
      setShowTermsError(true);
      toast.error("Please agree to the terms to continue.");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Use XMLHttpRequest to avoid Emergent preview script fetch interception
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/auth/signup`);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        xhr.timeout = 30000; // 30 second timeout
        
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            text: xhr.responseText
          });
        };
        
        xhr.onerror = () => {
          console.error("Signup XHR onerror triggered");
          reject(new Error("Network error - please check your connection"));
        };
        
        xhr.ontimeout = () => {
          console.error("Signup XHR timeout triggered");
          reject(new Error("Request timed out - please try again"));
        };
        
        xhr.send(JSON.stringify({ 
          email, 
          password, 
          name: name || undefined,
          legal_consent_accepted: true
        }));
      });
      
      if (!result.ok) {
        let errorMessage = "Signup failed. Please try again.";
        try {
          const errorData = JSON.parse(result.text);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          // Response wasn't valid JSON
        }
        throw new Error(errorMessage);
      }
      
      toast.success("Account created! Welcome to Trade Office.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message || "Signup failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        toast.success("If this email exists, a reset link has been sent.");
        setAuthMode("login");
      }
    } catch (error) {
      toast.error("Failed to send reset email");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowAuthModal(false);
    setEmail("");
    setPassword("");
    setName("");
    setAuthMode("login");
    setAgreedToTerms(false);
    setShowTermsError(false);
  };

  const switchMode = (mode) => {
    setEmail("");
    setPassword("");
    setName("");
    setAuthMode(mode);
    setAgreedToTerms(false);
    setShowTermsError(false);
  };

  const features = [
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Voice Notes",
      description: "Describe the job verbally and let AI transcribe it instantly"
    },
    {
      icon: <PenTool className="w-8 h-8" />,
      title: "Smart Questionnaire",
      description: "Answer a few quick questions and get accurate quotes every time"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Auto Pricing",
      description: "Real-time prices from Screwfix and automatic margin calculation"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Custom Branding",
      description: "Add your logo, set hourly rates, and customize quotes"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Auth Modal - Inlined to prevent re-render issues */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-card border-white/10 relative">
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground z-10"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
            
            <CardContent className="pt-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-primary rounded-md flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-7 h-7 text-primary-foreground" />
                </div>
                <h2 className="text-2xl font-bold">
                  {authMode === "login" && "Welcome Back"}
                  {authMode === "signup" && "Create Account"}
                  {authMode === "forgot" && "Reset Password"}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {authMode === "login" && "Sign in to your account"}
                  {authMode === "signup" && "Start your free trial today"}
                  {authMode === "forgot" && "Enter your email to reset"}
                </p>
              </div>

              {/* Google Button */}
              {authMode !== "forgot" && (
                <>
                  <Button
                    onClick={handleGoogleLogin}
                    disabled={isLoggingIn}
                    variant="outline"
                    className="w-full h-12 mb-4"
                    type="button"
                    data-testid="google-auth-btn"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </Button>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">or</span>
                    </div>
                  </div>
                </>
              )}

              {/* Email Form */}
              <form onSubmit={
                authMode === "login" ? handleEmailLogin : 
                authMode === "signup" ? handleEmailSignup : 
                handleForgotPassword
              }>
                {authMode === "signup" && (
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="name">Name (optional)</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="pl-10 h-12 bg-input/50 border-white/10"
                        autoComplete="name"
                        data-testid="signup-name"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2 mb-4">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="pl-10 h-12 bg-input/50 border-white/10"
                      required
                      autoComplete="email"
                      data-testid="auth-email"
                    />
                  </div>
                </div>

                {authMode !== "forgot" && (
                  <div className="space-y-2 mb-6">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={authMode === "signup" ? "Min 8 characters" : "Your password"}
                        className="pl-10 pr-10 h-12 bg-input/50 border-white/10"
                        required
                        minLength={authMode === "signup" ? 8 : undefined}
                        autoComplete={authMode === "signup" ? "new-password" : "current-password"}
                        data-testid="auth-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Legal Consent Checkbox - Only shown on signup */}
                {authMode === "signup" && (
                  <div className="mb-6">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms-consent"
                        checked={agreedToTerms}
                        onCheckedChange={(checked) => {
                          setAgreedToTerms(checked);
                          if (checked) setShowTermsError(false);
                        }}
                        className={`mt-0.5 ${showTermsError ? 'border-red-500' : ''}`}
                        data-testid="terms-consent-checkbox"
                      />
                      <div>
                        <Label 
                          htmlFor="terms-consent" 
                          className="text-sm leading-relaxed cursor-pointer"
                          style={{ fontSize: '12px' }}
                        >
                          I agree to the{' '}
                          <Link 
                            to="/terms" 
                            target="_blank"
                            className="text-orange-500 hover:text-orange-400 hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Terms &amp; Conditions
                          </Link>
                          {' '}and{' '}
                          <Link 
                            to="/privacy" 
                            target="_blank"
                            className="text-orange-500 hover:text-orange-400 hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </Link>
                          {' '}for Trade Office Solutions Limited.
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1.5">
                          Joining 100s of other plumbers who quote 5x faster.
                        </p>
                      </div>
                    </div>
                    {showTermsError && (
                      <p className="text-xs text-red-500 mt-2 ml-7">
                        Please agree to the terms to continue.
                      </p>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting || (authMode === "signup" && !agreedToTerms)}
                  className="w-full h-12 bg-primary text-primary-foreground"
                  data-testid="auth-submit"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : authMode === "login" ? (
                    "Sign In"
                  ) : authMode === "signup" ? (
                    "Create Account"
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>

              {/* Footer Links */}
              <div className="mt-6 text-center text-sm">
                {authMode === "login" && (
                  <>
                    <button
                      type="button"
                      onClick={() => switchMode("forgot")}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Forgot password?
                    </button>
                    <div className="mt-3">
                      <span className="text-muted-foreground">Don&apos;t have an account? </span>
                      <button
                        type="button"
                        onClick={() => switchMode("signup")}
                        className="text-primary hover:underline font-medium"
                      >
                        Sign up
                      </button>
                    </div>
                  </>
                )}
                {authMode === "signup" && (
                  <div>
                    <span className="text-muted-foreground">Already have an account? </span>
                    <button
                      type="button"
                      onClick={() => switchMode("login")}
                      className="text-primary hover:underline font-medium"
                    >
                      Sign in
                    </button>
                  </div>
                )}
                {authMode === "forgot" && (
                  <button
                    type="button"
                    onClick={() => switchMode("login")}
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to sign in
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Hero Section */}
      <div className="hero-gradient min-h-screen flex flex-col">
        {/* Nav */}
        <nav className="flex items-center justify-between p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="text-2xl font-black tracking-tight">Trade Office</span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost"
              onClick={() => navigate("/pricing")}
              className="hidden sm:flex"
              data-testid="nav-pricing-link"
            >
              Pricing
            </Button>
            <Button 
              data-testid="nav-login-btn"
              onClick={() => setShowAuthModal(true)}
              disabled={isLoggingIn}
              className="h-12 px-6 font-bold uppercase tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
            >
              {isLoggingIn ? "Connecting..." : "Sign In"}
              <LogIn className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="flex-1 flex items-center justify-center px-6 md:px-8">
          <div className="max-w-4xl mx-auto text-center stagger-children">
            <span className="inline-block px-4 py-2 bg-secondary rounded-full text-sm font-medium text-muted-foreground mb-6">
              Built for Plumbers & Trade Professionals
            </span>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6">
              Stop Quoting at Midnight.
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Turn messy part lists into professional quotes in 60 seconds. Get your evenings back with UK-merchant pricing at your fingertips.
            </p>
            
            <div className="flex flex-col items-center justify-center gap-3">
              <Button 
                data-testid="hero-get-started-btn"
                onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
                disabled={isLoggingIn}
                size="lg"
                className="h-14 px-8 text-lg font-bold uppercase tracking-wide text-black hover:opacity-90 btn-glow w-full sm:w-auto"
                style={{ backgroundColor: '#FF8C00' }}
              >
                Start My Free Trial
                <ChevronRight className="w-6 h-6 ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground">
                No Credit Card Required | 30-Second Signup
              </p>
            </div>
          </div>
        </div>

        {/* Product Demo Video */}
        <div className="px-6 md:px-8 pb-12">
          <div className="max-w-4xl mx-auto">
            <div style={{ position: 'relative', aspectRatio: '4/3' }} className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              <iframe
                loading="lazy"
                title="Gumlet video player"
                src="https://play.gumlet.io/embed/698a68ebfc23d3d76fa9772d?background=1&muted=1&loop=1&autoplay=1"
                style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                referrerPolicy="origin"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen"
                data-testid="product-demo-video"
              />
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              See how easy it is to create professional quotes
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="px-6 md:px-8 pb-16">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-card-${index}`}
                className="bg-card border border-white/5 rounded-lg p-6 hover:border-primary/50 transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-secondary rounded-lg flex items-center justify-center mb-4 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <section className="py-20 px-6 md:px-8 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">How It Works</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Three simple steps to professional quotes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Describe the Job", desc: "Use voice or text to describe the work needed" },
              { step: "02", title: "AI Generates Quote", desc: "Our AI identifies materials, estimates labor, and calculates pricing" },
              { step: "03", title: "Send to Customer", desc: "Review, customize, and send your professional quote instantly" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <span className="text-6xl font-black text-primary/20">{item.step}</span>
                <h3 className="text-xl font-bold mt-2 mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
              Simple Pricing
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Pay for What You Use
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              No hidden fees. No contracts. Start with pay-as-you-go or save more with a subscription.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* PAYG */}
            <Card 
              className="bg-card border-white/10 hover:border-primary/50 transition-all cursor-pointer group" 
              data-testid="landing-pricing-payg"
              onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
            >
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  <Zap className="w-6 h-6 text-yellow-500" />
                </div>
                <h3 className="font-bold text-lg mb-1">Pay As You Go</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold">£9</span>
                  <span className="text-muted-foreground">/quote</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2 text-left mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    No commitment
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    Full AI-powered quotes
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    Live Screwfix pricing
                  </li>
                </ul>
                <div className="text-xs text-primary font-medium group-hover:underline">
                  Click to get started →
                </div>
              </CardContent>
            </Card>

            {/* Starter */}
            <Card 
              className="bg-card border-white/10 hover:border-primary/50 transition-all cursor-pointer group" 
              data-testid="landing-pricing-starter"
              onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
            >
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  <TrendingUp className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="font-bold text-lg mb-1">Starter</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold">£59</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2 text-left mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    25 quotes/month
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    £2.36 per quote
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    Save 74%
                  </li>
                </ul>
                <div className="text-xs text-primary font-medium group-hover:underline">
                  Click to get started →
                </div>
              </CardContent>
            </Card>

            {/* Professional */}
            <Card 
              className="bg-card border-primary/50 hover:border-primary transition-all relative cursor-pointer group" 
              data-testid="landing-pricing-pro"
              onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
            >
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
                Most Popular
              </Badge>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/30 transition-colors">
                  <Crown className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-1">Professional</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold">£99</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2 text-left mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    75 quotes/month
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    £1.32 per quote
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    Save 85%
                  </li>
                </ul>
                <div className="text-xs text-primary font-medium group-hover:underline">
                  Click to get started →
                </div>
              </CardContent>
            </Card>

            {/* Business */}
            <Card 
              className="bg-card border-white/10 hover:border-primary/50 transition-all cursor-pointer group" 
              data-testid="landing-pricing-business"
              onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
            >
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <h3 className="font-bold text-lg mb-1">Business</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold">£150</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-2 text-left mb-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    150 quotes/month
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    £1.00 per quote
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary" />
                    Save 89%
                  </li>
                </ul>
                <div className="text-xs text-primary font-medium group-hover:underline">
                  Click to get started →
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
              size="lg"
              className="h-14 px-8 bg-primary text-primary-foreground"
              data-testid="pricing-cta-btn"
            >
              Start Your Free Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-muted-foreground text-sm mt-4">
              3 free quotes included • No credit card required
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-8 bg-primary/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Save Hours on Quoting?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of plumbers who are already creating professional quotes in seconds instead of hours.
          </p>
          <Button 
            onClick={() => { setShowAuthModal(true); setAuthMode("signup"); }}
            size="lg"
            className="h-14 px-8 bg-primary text-primary-foreground btn-glow"
            data-testid="footer-cta-btn"
          >
            Get Started Free
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-8 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold">Trade Office</span>
            </div>
            <div className="flex items-center gap-6">
              <Link 
                to="/terms" 
                className="text-sm text-muted-foreground hover:text-orange-500 transition-colors"
                data-testid="footer-terms-link"
              >
                Terms &amp; Conditions
              </Link>
              <Link 
                to="/privacy" 
                className="text-sm text-muted-foreground hover:text-orange-500 transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Trade Office Solutions Limited. All rights reserved.
            </p>
          </div>
          <div className="border-t border-white/5 pt-4 text-center">
            <p className="text-xs text-muted-foreground">
              TRADE OFFICE SOLUTIONS LTD | Company Number: 16896413
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              30 Grange Close, Leighton Buzzard, England, LU7 2PW
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
